import { useCallback, useMemo } from 'react';
import { cloneDeep } from 'lodash';
import styled from 'styled-components';
import { ScenarioConstants } from 'app/modules/rules/models';
import { WidgetProps } from 'app/modules/detectionModels/components/scenarioWidgets/models';
import {
  U21Checkbox,
  U21FormLabel,
  U21LoadError,
  U21Spacer,
  U21Table,
  U21TableBodyCellProps,
  U21TableColumn,
  U21TextField,
  U21Typography,
} from 'app/shared/u21-ui/components';
import { TimeDeltaInput } from 'app/modules/detectionModels/components/scenarioWidgets/TimeDeltaInput';
import { useGetMatchlist } from 'app/modules/matchlist/queries/useGetMatchlist';
import { fromUpperToSentenceCase } from 'app/shared/utils/string';
import { ScenarioParameters } from 'app/modules/detectionModels/models';

interface FieldRow {
  fieldName: string;
  mustMatch: boolean;
  atLeastNMustMatch: boolean;
  fuzzyMatch: number;
  timeDiff?: number;
}

interface Field {
  mustMatch: boolean;
  atLeastNMustMatch: boolean;
  fuzzyMatch: number;
  timeDiff?: number;
}

/*
This widget stores match condition fields in editingScenario.parameters[MATCH_CONDITIONS].

Field data for ALL fields (including currently disabled ones) is preserved in
MATCH_CONDITIONS.fields so that toggling blacklist types doesn't lose user input.

`mustMatch` and `atLeastNMustMatch` checkboxes for a field are mutually exclusive.
*/

const GENERAL_FIELDS = ['street', 'city', 'state', 'zip_code', 'country'];

const USER_FIELDS = [
  'first_name',
  'middle_name',
  'last_name',
  'alias_first_name',
  'alias_middle_name',
  'alias_last_name',
  'ssn',
  'date_of_birth',
];

const DATE_FIELDS = ['date_of_birth'];

const BUSINESS_FIELDS = ['business_name', 'dba_name', 'corporate_tax_id'];

const ALL_FIELDS = [...GENERAL_FIELDS, ...USER_FIELDS, ...BUSINESS_FIELDS];

const INITIAL_FIELDS: Record<string, Field> = ALL_FIELDS.reduce(
  (acc, field) => {
    acc[field] = DATE_FIELDS.includes(field)
      ? {
          mustMatch: false,
          atLeastNMustMatch: false,
          fuzzyMatch: 100,
          timeDiff: 0,
        }
      : { mustMatch: false, atLeastNMustMatch: false, fuzzyMatch: 100 };
    return acc;
  },
  {} as Record<string, Field>,
);

const validateMatchConditions = (
  matchConditions: ScenarioParameters['match_conditions'],
  enabledFields: string[],
): boolean => {
  if (!matchConditions) {
    return false;
  }
  const numberMustMatch = matchConditions.numberMustMatch || '';
  const fields = matchConditions.fields || {};
  const hasAtLeastN = enabledFields.some((f) => fields[f]?.atLeastNMustMatch);
  if (!hasAtLeastN) {
    return true;
  }
  return (parseInt(numberMustMatch, 10) || 0) >= 1;
};

export const ScenarioPerBlacklistEditor = ({
  viewOnly,
  editingScenario,
  onChange,
}: WidgetProps) => {
  const {
    data: matchlist,
    isError,
    isLoading,
    refetch,
  } = useGetMatchlist(
    editingScenario.parameters[ScenarioConstants.BLACKLIST_ID],
  );

  const enabledFields = useMemo(() => {
    if (!matchlist) {
      return [];
    }
    return [
      ...(matchlist.type === null ? [] : GENERAL_FIELDS),
      ...(matchlist.type === 'USER' ? USER_FIELDS : []),
      ...(matchlist.type === 'BUSINESS' ? BUSINESS_FIELDS : []),
    ];
  }, [matchlist]);

  const matchConditions = useMemo(
    () => editingScenario.parameters[ScenarioConstants.MATCH_CONDITIONS] || {},
    [editingScenario],
  );

  const fields = useMemo(
    () => ({ ...INITIAL_FIELDS, ...matchConditions.fields }),
    [matchConditions],
  );

  const updateMatchConditions = useCallback(
    (newMatchConditions: ScenarioParameters['match_conditions']) => {
      const isValid = validateMatchConditions(
        newMatchConditions,
        enabledFields,
      );
      onChange({
        ...cloneDeep(editingScenario),
        parameters: {
          ...editingScenario.parameters,
          [ScenarioConstants.MATCH_CONDITIONS]: newMatchConditions,
          [ScenarioConstants.MATCH_CONDITIONS_VALIDATION_RESULT]: isValid,
        },
      });
    },
    [editingScenario, enabledFields, onChange],
  );

  const tableData: FieldRow[] = useMemo(
    () =>
      enabledFields.map((fieldName) => ({
        fieldName,
        ...fields[fieldName],
      })),
    [enabledFields, fields],
  );

  const columns: U21TableColumn<FieldRow, any>[] = useMemo(
    () => [
      {
        id: 'fieldName',
        accessor: (row) => {
          if (row.fieldName === 'ssn') {
            return 'SSN';
          }
          return fromUpperToSentenceCase(row.fieldName);
        },
        Header: 'Field',
      },
      {
        id: 'mustMatch',
        accessor: 'mustMatch',
        Header: 'All must match',
        Cell: ({ row }: U21TableBodyCellProps<FieldRow>) => (
          <U21Checkbox
            disabled={viewOnly || row.atLeastNMustMatch}
            checked={row.mustMatch}
            onChange={(checked) => {
              const newFields: Record<string, Field> = {
                ...fields,
                [row.fieldName]: {
                  ...fields[row.fieldName],
                  mustMatch: checked,
                },
              };
              updateMatchConditions({ ...matchConditions, fields: newFields });
            }}
          />
        ),
      },
      {
        id: 'atLeastNMustMatch',
        accessor: 'atLeastNMustMatch',
        Header: () => (
          <>
            <U21Typography variant="subtitle2" color="text.secondary" inline>
              At least
            </U21Typography>
            <StyledNumberInput
              disabled={viewOnly}
              value={matchConditions.numberMustMatch || ''}
              onChange={(value) =>
                updateMatchConditions({
                  ...matchConditions,
                  numberMustMatch: value ? String(value) : '',
                })
              }
              type="integer"
            />
            <U21Typography variant="subtitle2" color="text.secondary" inline>
              must match
            </U21Typography>
          </>
        ),
        Cell: ({ row }: U21TableBodyCellProps<FieldRow>) => (
          <U21Checkbox
            disabled={viewOnly || row.mustMatch}
            checked={row.atLeastNMustMatch}
            onChange={(checked) => {
              updateMatchConditions({
                ...matchConditions,
                fields: {
                  ...fields,
                  [row.fieldName]: {
                    ...fields[row.fieldName],
                    atLeastNMustMatch: checked,
                  },
                },
              });
            }}
          />
        ),
      },

      {
        id: 'fuzzyMatch',
        accessor: 'fuzzyMatch',
        Header: 'Fuzzy match',
        Cell: ({ row }: U21TableBodyCellProps<FieldRow>) =>
          DATE_FIELDS.includes(row.fieldName) ? (
            <U21Spacer>
              <U21FormLabel>Date is within</U21FormLabel>
              <TimeDeltaInput
                value={row.timeDiff || 0}
                onChange={(value) => {
                  updateMatchConditions({
                    ...matchConditions,
                    fields: {
                      ...fields,
                      [row.fieldName]: {
                        ...fields[row.fieldName],
                        timeDiff: value,
                      },
                    },
                  });
                }}
                viewOnly={viewOnly}
              />
            </U21Spacer>
          ) : (
            <U21TextField
              clearable={false}
              disabled={viewOnly}
              value={row.fuzzyMatch}
              onChange={(value: number = 0) => {
                updateMatchConditions({
                  ...matchConditions,
                  fields: {
                    ...fields,
                    [row.fieldName]: {
                      ...fields[row.fieldName],
                      fuzzyMatch: value,
                    },
                  },
                });
              }}
              type="integer"
            />
          ),
      },
    ],
    [fields, matchConditions, updateMatchConditions, viewOnly],
  );

  if (!editingScenario.parameters[ScenarioConstants.BLACKLIST_ID]) {
    return null;
  }

  if (isError) {
    return <U21LoadError label="matchlist" onTryAgain={() => refetch()} />;
  }

  return (
    <StyledU21Table
      columns={columns}
      count={tableData.length}
      data={tableData}
      disableSortBy
      // parent has some absolute positioning which sticky header does not work well with
      disableStickyHeader
      loading={isLoading}
    />
  );
};

const StyledU21Table = styled(U21Table<FieldRow>)`
  // parent component sets to text-align center so reset it
  text-align: start;
`;

const StyledNumberInput = styled(U21TextField)`
  flex: 0.8;
  min-width: 60px;
  margin: 0 8px;
  display: inline-flex;
`;
